import useLocale from "@/vue/composables/useLocale";
import getSiteFromDomain from "@/vue/planning/logic/getSiteFromDomain";
import { IStoreState } from "../state/state";

interface FeeRow {
	name: string;
	wiypName: string;
	value: number;
}

interface IFeeConfig {
	[locale: string]: FeeRow[];
}

// WIYP fee ids → summary rows. "Total fees" is the sum and is not shown as its own line.
const WIYP_FEE_IDS: Record<string, string> = {
	"Fixed Fees": "fileFees",
	"Calamity Fees": "NL_Calamiteitenfonds",
	"SGR Fees": "NL_SGRcontribution",
};

const fileFee = (): FeeRow => ({
	name: "fileFees",
	wiypName: "fileFees_WIYP",
	value: 24,
});

const dutchFees = (participants: number[]): FeeRow[] => [
	fileFee(),
	{
		name: "NL_Calamiteitenfonds",
		wiypName: "NL_Calamiteitenfonds_WIYP",
		value:
			participants.length < 10 ? 2.5 : participants.length < 19 ? 5 : 7.5,
	},
	{
		name: "NL_SGRcontribution",
		wiypName: "NL_SGRcontribution_WIYP",
		value: 5 * participants.filter((age) => age > 1).length,
	},
];

// befr.yoonly.eu (fr_FR_YN) uses the French file fee, same as www.travelski.com.
// benl.yoonly.eu (nl_NL_YN) uses the Dutch fees, same as www.nl.travelski.com.
const resolveFeeLocale = (site: string, locale: string): string => {
	if (site === "www.befr.yoonly.eu" || locale === "fr_FR_YN") return "fr_FR";
	if (site === "www.benl.yoonly.eu" || locale === "nl_NL_YN") return "nl_NL";
	return locale;
};

const applyWiypFees = (
	rows: FeeRow[],
	wiypFees: { id?: string; price?: number }[]
): void => {
	if (!rows.length || !wiypFees.length) return;

	let appliedNamedFee = false;
	wiypFees.forEach((fee) => {
		const name = fee.id ? WIYP_FEE_IDS[fee.id] : undefined;
		if (!name || typeof fee.price !== "number") return;
		const row = rows.find((item) => item.name === name);
		if (!row) return;
		row.value = fee.price;
		appliedNamedFee = true;
	});

	if (!appliedNamedFee && typeof wiypFees[0].price === "number") {
		rows[0].value = wiypFees[0].price;
	}
};

export default {
	fees: (state: IStoreState) => {
		const site = getSiteFromDomain(window.location.hostname);
		const locale = (useLocale() as string) || "";
		const lang = resolveFeeLocale(site, locale);
		const participants = state.participants || [];

		const feeConfig: IFeeConfig = {
			fr_FR: [fileFee()],
			nl_NL: dutchFees(participants),
			en_US: [
				{
					name: "fileFees",
					wiypName: "fileFees_WIYP",
					value: 49,
				},
			],
		};

		const rows = feeConfig[lang];
		if (!rows) return [];

		const wiypFees = state.wiypFees || [];
		const isYoonlyBe =
			site === "www.befr.yoonly.eu" ||
			site === "www.benl.yoonly.eu" ||
			locale === "fr_FR_YN" ||
			locale === "nl_NL_YN";

		// Yoonly prices come back with the fee breakdown. Copy those amounts onto the rows.
		if (isYoonlyBe) {
			applyWiypFees(rows, wiypFees);
		} else if (wiypFees.length > 0 && typeof wiypFees[0].price === "number") {
			rows[0].value = wiypFees[0].price;
		}

		return rows;
	},
};
